/**
 * Pedersen DKG polynomial helpers — pure math, no protocol state.
 *
 * Conventions:
 *  - Polynomials are arrays of `Scalar` (bigint) coefficients
 *    `[a_0, a_1, ..., a_{t-1}]` representing `f(x) = a_0 + a_1 x + ... +
 *    a_{t-1} x^{t-1}`. Length = threshold.
 *  - All arithmetic is in Z_q where q = GROUP_ORDER (Ristretto255).
 *  - Evaluation points (recipient indices) are 1-based positive integers.
 *
 * STATUS: M2 (DKG). Lagrange interpolation lands in M5 (tally combine).
 */
import { ristretto255 } from "@noble/curves/ed25519";

import type { RistrettoPoint, Scalar } from "../types";
import { GROUP_ORDER, randomScalar } from "./encoding";

/**
 * Sample a fresh degree-(threshold-1) polynomial. The constant term
 * `a_0` is the trustee's contribution to the combined election private
 * key — uniform random in [1, q-1] (rejecting 0 makes the trustee's
 * commitment `g^{a_0} != identity`, which downstream verifiers refuse).
 */
export function samplePolynomial(threshold: number): Scalar[] {
  if (threshold < 1) {
    throw new Error("threshold must be >= 1");
  }
  const coeffs: Scalar[] = [];
  for (let k = 0; k < threshold; k++) {
    coeffs.push(randomScalar());
  }
  return coeffs;
}

/**
 * Evaluate `f(x) = sum_{k=0}^{t-1} coefficients[k] * x^k` mod q via
 * Horner's method.
 *
 * Time complexity O(t); allocations: 0 beyond the result. Constant-time
 * in `coefficients` (does not branch on coefficient values).
 */
export function evaluatePolynomial(
  coefficients: ReadonlyArray<Scalar>,
  x: Scalar,
): Scalar {
  if (coefficients.length === 0) {
    throw new Error("polynomial must have at least one coefficient");
  }
  const xMod = ((x % GROUP_ORDER) + GROUP_ORDER) % GROUP_ORDER;
  let acc = 0n;
  for (let k = coefficients.length - 1; k >= 0; k--) {
    acc = (acc * xMod + coefficients[k]!) % GROUP_ORDER;
  }
  return acc;
}

/**
 * Feldman commitments to the polynomial: `[g^{a_0}, g^{a_1}, ...,
 * g^{a_{t-1}}]`. Public — published by the dealing trustee. Anyone uses
 * them to verify shares against the dealer.
 */
export function feldmanCommitments(
  coefficients: ReadonlyArray<Scalar>,
): RistrettoPoint[] {
  return coefficients.map((a) => ristretto255.Point.BASE.multiply(a));
}

/**
 * Verify that `share == f_dealer(recipientIndex)` is consistent with
 * the dealer's published Feldman commitments, without ever needing the
 * dealer's polynomial coefficients.
 *
 * Math: `g^share ?= prod_{k=0}^{t-1} commitments[k]^{recipientIndex^k}`,
 * because `commitments[k] = g^{a_k}` and the product evaluates to
 * `g^{f_dealer(recipientIndex)}`.
 *
 * `recipientIndex` MUST be the 1-based trustee index (matching
 * `Trustee.index` in the transcript), NOT a UUID or array offset.
 */
export function verifyShareAgainstFeldman(
  share: Scalar,
  recipientIndex: number,
  commitments: ReadonlyArray<RistrettoPoint>,
): boolean {
  if (!Number.isInteger(recipientIndex) || recipientIndex < 1) {
    throw new Error("recipientIndex must be a positive integer");
  }
  if (commitments.length === 0) {
    throw new Error("commitments array is empty");
  }
  const lhs = ristretto255.Point.BASE.multiply(normalize(share));
  const rhs = combineCommitments(commitments, BigInt(recipientIndex));
  return lhs.equals(rhs);
}

/**
 * `prod_{k=0}^{t-1} commitments[k]^{x^k}` — used by both share
 * verification and verification-key derivation. Public-only inputs;
 * `multiplyUnsafe` is acceptable.
 */
export function combineCommitments(
  commitments: ReadonlyArray<RistrettoPoint>,
  x: Scalar,
): RistrettoPoint {
  const xMod = normalize(x);
  let acc: RistrettoPoint = ristretto255.Point.ZERO;
  let xPow = 1n;
  for (let k = 0; k < commitments.length; k++) {
    acc = acc.add(commitments[k]!.multiplyUnsafe(xPow));
    xPow = (xPow * xMod) % GROUP_ORDER;
  }
  return acc;
}

function normalize(s: Scalar): Scalar {
  return ((s % GROUP_ORDER) + GROUP_ORDER) % GROUP_ORDER;
}
